import React from "react";

import ImportWitnessCapsule, { type ImportWitnessCapsuleProps } from "./ImportWitnessCapsule";

const INLINE_WITNESS_TICKER_STYLE_ID = "inline-witness-ticker-keyframes";

const INLINE_WITNESS_ACTIVITY_REFERENCE_ITEMS = [
  "Timeline playhead tracking live",
  "Clip selection handoff ready",
  "Focus panel sync armed",
  "OmniStudio compile preview queued",
  "OmniStudio apply bridge standing by",
  "Cue and marker review monitoring",
  "Witness route continuity locked",
  "Editorial focus lane ready for action",
] as const;

function ensureInlineWitnessTickerStyle() {
  if (typeof document === "undefined") return;
  if (document.getElementById(INLINE_WITNESS_TICKER_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = INLINE_WITNESS_TICKER_STYLE_ID;
  style.textContent = `
    @keyframes inline-witness-ticker {
      0% { transform: translate3d(0, 0, 0); }
      100% { transform: translate3d(-50%, 0, 0); }
    }
  `;
  document.head.appendChild(style);
}

function buildInlineWitnessTickerItems(text: string, tone: "blue" | "orange" | "red"): string[] {
  const baseItems = text
    .split(/\s{2,}[•|]\s{2,}|\s+[•|]\s+/)
    .map((item) => item.trim())
    .filter(Boolean);

  const toneLead =
    tone === "red"
      ? "Attention path elevated"
      : tone === "orange"
        ? "Transition handoff in progress"
        : "Signal path steady and ready";

  return Array.from(new Set([toneLead, ...baseItems, ...INLINE_WITNESS_ACTIVITY_REFERENCE_ITEMS]));
}

export type ImportWitnessCapsuleViewModel = {
  id: string;
  mediaType: ImportWitnessCapsuleProps["mediaType"];
  label: string;
  durationSec: number;
  currentTimeSec?: number;
  isFocused: boolean;
  isSelected: boolean;
  isPinned?: boolean;
  status?: ImportWitnessCapsuleProps["status"];
  sourceId: string;
  clipId?: string;
  tickerText?: string;
  tickerTone?: "blue" | "orange" | "red";
  bindingMode?: ImportWitnessCapsuleProps["bindingMode"];
  hasLinkedAudio?: boolean;
  hasLinkedVideo?: boolean;
};

export interface ImportWitnessRowProps {
  capsules: ImportWitnessCapsuleViewModel[];
  activeId?: string;
  onFocusCapsule: (id: string) => void;
  onOpenWitnessBar: (id: string) => void;
  onDismissCapsule?: (id: string) => void;
  className?: string;
  style?: React.CSSProperties;
}

function ImportWitnessTicker({
  text,
  tone = "blue",
}: {
  text: string;
  tone?: "blue" | "orange" | "red";
}): JSX.Element {
  ensureInlineWitnessTickerStyle();

  const tickerBorder =
    tone === "red"
      ? "rgba(248, 113, 113, 0.82)"
      : tone === "orange"
        ? "rgba(251, 146, 60, 0.78)"
        : "rgba(56, 189, 248, 0.78)";
  const tickerGlow =
    tone === "red"
      ? "rgba(239, 68, 68, 0.28)"
      : tone === "orange"
        ? "rgba(249, 115, 22, 0.28)"
        : "rgba(14, 165, 233, 0.28)";
  const tickerTextColor =
    tone === "red"
      ? "rgba(254, 226, 226, 0.99)"
      : tone === "orange"
        ? "rgba(255, 237, 213, 0.98)"
        : "rgba(224, 242, 254, 0.98)";
  const tickerBackground =
    tone === "red"
      ? "linear-gradient(180deg, rgba(255,255,255,0.12), rgba(82, 11, 11, 0.3) 28%, rgba(18, 8, 12, 0.9))"
      : "linear-gradient(180deg, rgba(255,255,255,0.12), rgba(8, 47, 73, 0.24) 28%, rgba(12, 18, 28, 0.88))";
  const tickerItems = buildInlineWitnessTickerItems(text, tone);
  const tickerLine = tickerItems.join("   //   ");
  const estimatedCharacterCount = tickerLine.length;
  const animationDurationSec = Math.max(46.8, Math.min(88.4, estimatedCharacterCount * 0.468));

  return (
    <>
      <div
        className="relative h-7 w-[clamp(260px,34vw,440px)] flex-none overflow-hidden rounded-full border px-3"
        aria-live="polite"
        style={{
          borderColor: tickerBorder,
          borderStyle: "dotted",
          background: tickerBackground,
          boxShadow: `inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(255,255,255,0.04), 0 0 16px ${tickerGlow}, 0 10px 28px rgba(0,0,0,0.18)`,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}
      >
        <span
          className="pointer-events-none absolute inset-[1px] rounded-full"
          aria-hidden
          style={{
            background: "linear-gradient(120deg, rgba(255,255,255,0.14), rgba(255,255,255,0.04) 36%, rgba(255,255,255,0) 72%)",
            maskImage: "linear-gradient(180deg, rgba(0,0,0,0.9), rgba(0,0,0,0.7))",
          }}
        />
        <span
          className="pointer-events-none absolute inset-y-0 left-0 right-0 overflow-hidden"
          style={{
            display: "flex",
            alignItems: "center",
            color: tickerTextColor,
            fontSize: "8.5px",
            fontWeight: 700,
            letterSpacing: "0.16em",
            textShadow: tone === "red" ? "0 0 12px rgba(248, 113, 113, 0.26)" : "0 0 10px rgba(56, 189, 248, 0.22)",
          }}
        >
          <span
            className="block overflow-hidden whitespace-nowrap"
            style={{
              width: "100%",
              paddingInline: "0.75rem",
              maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
            }}
          >
            <span
              className="inline-flex min-w-max items-center gap-10 whitespace-nowrap"
              style={{ animation: `inline-witness-ticker ${animationDurationSec}s linear infinite` }}
            >
              <span>{tickerLine}</span>
              <span aria-hidden="true">{tickerLine}</span>
            </span>
          </span>
        </span>
      </div>
    </>
  );
}

export default function ImportWitnessRow({
  capsules,
  activeId,
  onFocusCapsule,
  onOpenWitnessBar,
  onDismissCapsule,
  className = "",
  style,
}: ImportWitnessRowProps): JSX.Element | null {
  if (!capsules.length) return null;

  const rowRef = React.useRef<HTMLDivElement | null>(null);
  const activeCapsuleRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!activeCapsuleRef.current || !rowRef.current) return;

    activeCapsuleRef.current.scrollIntoView({
      block: "nearest",
      inline: "center",
      behavior: "smooth",
    });
  }, [activeId, capsules]);

  return (
    <div
      ref={rowRef}
      data-oc-import-witness-row="1"
      className={`flex items-center gap-1.5 overflow-x-auto overflow-y-hidden px-1 py-1 scrollbar-thin scrollbar-track-transparent ${className}`.trim()}
      style={style}
    >
      {capsules.map((capsule) => {
        const showTicker = Boolean(capsule.tickerText);
        const isActive = capsule.id === activeId || capsule.isFocused || capsule.isSelected;

        return (
          <div
            key={capsule.id}
            ref={isActive ? activeCapsuleRef : null}
            className={showTicker ? "flex min-w-0 items-center gap-2 transition-transform duration-200" : "flex flex-none items-center transition-transform duration-200"}
            style={showTicker ? { minWidth: isActive ? 460 : 400, transform: isActive ? "translateY(-1px)" : "translateY(0)" } : { transform: isActive ? "translateY(-1px)" : "translateY(0)" }}
          >
            <ImportWitnessCapsule
              {...capsule}
              isFocused={capsule.id === activeId || capsule.isFocused}
              isSelected={capsule.id === activeId || capsule.isSelected}
              onFocus={onFocusCapsule}
              onOpenWitnessBar={onOpenWitnessBar}
              onDismiss={isActive ? onDismissCapsule : undefined}
            />
            {showTicker ? <ImportWitnessTicker text={capsule.tickerText!} tone={capsule.tickerTone} /> : null}
          </div>
        );
      })}
    </div>
  );
}